import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { useCallback } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { tracking } from "~/data";
import { getAbsoluteUrl } from "~/lib/utils";
import type { mostPopularQueueQuery$key } from "~/queries/__generated__/mostPopularQueueQuery.graphql";

import {
  Container,
  DisplayNumber,
  MostPopularItem,
  MostPopularItems,
  StyledContentItemHomeSecondary,
  Title,
  TrendingTopicContainer,
} from "./styles";

type Props = {
  className?: string;
  reference: mostPopularQueueQuery$key;
  trendingTopicSlot?: ReactNode;
};

export const MostPopular: FunctionComponent<Props> = ({
  className,
  reference: reference_,
  trendingTopicSlot,
}) => {
  const data = useFragment(
    graphql`
      fragment mostPopularQueueQuery on Query
      @argumentDefinitions(mostPopularQueueName: { type: "String!" }) {
        mostPopularQueueQuery: queue(filter: { name: $mostPopularQueueName }) {
          items(first: 5) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...homeSecondaryContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const mostPopularItems = data?.mostPopularQueueQuery?.items?.edges;
  const listOfArticles = (mostPopularItems?.map(item => item.node.entityId) ?? []).join(",");

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/most_popular",
      category: "Homepage Depth",
    }),
    [],
  );
  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "most_popular",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    RecirculationWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    getGa4Event: impressionGA4Event,
    options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
  });

  if (mostPopularItems?.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.MostPopular,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>MOST POPULAR</Title>
        <MostPopularItems>
          {mostPopularItems?.map(({ node }, index) => (
            <MostPopularItem key={node.entityId}>
              <DisplayNumber>{index + 1}</DisplayNumber>
              <StyledContentItemHomeSecondary
                reference={node}
                onClick={(entityId, urlAlias) => {
                  sendGATracking({
                    action: "Click/most_popular",
                    category: "Homepage Depth",
                    label: entityId,
                  });
                  sendGA4Tracking({
                    action: "click",
                    category: "recirculation",
                    customized_parameters: {
                      article_id: entityId,
                      destination_url: getAbsoluteUrl(urlAlias),
                      page_type: "homepage",
                      widget_name: "most_popular",
                    },
                    subcategory: "widget",
                  });
                }}
              />
            </MostPopularItem>
          ))}
        </MostPopularItems>

        {trendingTopicSlot && <TrendingTopicContainer>{trendingTopicSlot}</TrendingTopicContainer>}
      </Container>
    </BaseLinkContextProvider>
  );
};

MostPopular.displayName = "MostPopular";
